/**
 * `MockChainRpcClient` —— 连 `web/server` 的**共享** Mock 链账本（WS JSON-RPC）。
 *
 * 与 `MockChainLocalClient`（T04 的降级实现）共同实现 `IChainClient`，
 * UI 层只认接口，切换点唯一在 `services/container.ts`（ARCH §1.3）。
 */

import {
  dpbToMicro,
  type Address,
  type Anchor,
  type AnchorKind,
  type AppConfig,
  type BehaviorRewardEvent,
  type Channel,
  type ContentPurchaseRecord,
  type ContentPurchaseReq,
  type ContentConfig,
  type DexCandle,
  type DexOrder,
  type DexPool,
  type DexTrade,
  type FeedItem,
  type IChainClient,
  type Micro,
  type PromoCreateReq,
  type PromoRedeemReq,
  type GrouponReq,
  type SubscriptionReq,
  type CrossReq,
  type StakeInterestReq,
  type ReportReq,
  type ReportResult,
  type AppealReq,
  type AppealResult,
  type ResolveResult,
  type TxHash,
  type TxRecord,
} from '@shared/chain';
import { MeshLinkError } from '@/lib/result';
import { isErrorCode, type ErrorCode } from '@shared/errors';
import { createLogger } from '@/lib/logger';
import { CHAIN_WS_URL } from '@/config';

const logger = createLogger('chain:rpc');

interface RpcResponse {
  id: number | string | null;
  ok: boolean;
  result?: unknown;
  error?: { code: string; message: string };
}

/**
 * A-4 守卫：把服务端回包中的 `string` 码收窄为已知 `ErrorCode`。
 *
 * 服务端 `LedgerError.code` 类型为裸 `string`，回包码**不被 TypeScript 保证**在
 * 客户端 catalog 集合内。旧实现用 `as never` 强行绕过类型检查，一旦服务端新增码而
 * 未同步 `shared/errors.ts`，`new MeshLinkError(unknownCode)` 会在构造体内
 * `errorCatalog[code].text` 处抛 **TypeError**（非 MeshLinkError）→ UI 二次降级 E_UNKNOWN。
 * 此处运行时校验：未知码安全回落 `E_UNKNOWN`，不再抛构造期异常。
 */
function normalizeRpcCode(raw: unknown): ErrorCode {
  return isErrorCode(raw) ? raw : 'E_UNKNOWN';
}

/** 把服务端错误码映射为本地 `MeshLinkError`。 */
function throwRpcError(res: RpcResponse): never {
  const code = normalizeRpcCode(res.error?.code);
  throw new MeshLinkError(code, res.error?.message, res.error);
}

export class MockChainRpcClient implements IChainClient {
  private ws: WebSocket | null = null;
  private seq = 0;
  private pending = new Map<number, { resolve: (v: unknown) => void; reject: (e: unknown) => void }>();
  private connectPromise: Promise<WebSocket> | null = null;
  private hello: string[] = [];

  constructor(private readonly url: string = CHAIN_WS_URL) {}

  get methods(): string[] {
    return this.hello;
  }

  /** 建立（或复用）连接。 */
  private async connect(): Promise<WebSocket> {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) return this.ws;
    if (this.connectPromise) return this.connectPromise;

    this.connectPromise = new Promise<WebSocket>((resolve, reject) => {
      let ws: WebSocket;
      try {
        ws = new WebSocket(this.url);
      } catch (err) {
        this.connectPromise = null;
        reject(new MeshLinkError('E_CHAIN_UNREACHABLE', undefined, err));
        return;
      }
      const timer = setTimeout(() => {
        try {
          ws.close();
        } catch {
          // 超时关闭失败可忽略
        }
        this.connectPromise = null;
        reject(new MeshLinkError('E_CHAIN_UNREACHABLE'));
      }, 4_000);

      ws.onopen = () => {
        clearTimeout(timer);
        this.ws = ws;
        resolve(ws);
      };
      ws.onmessage = (evt) => {
        try {
          const res = JSON.parse(String(evt.data)) as RpcResponse;
          if (res.id === null) {
            // 服务端 hello
            const result = res.result as { methods?: string[] } | undefined;
            if (result?.methods) this.hello = result.methods;
            return;
          }
          const entry = this.pending.get(Number(res.id));
          if (!entry) return;
          this.pending.delete(Number(res.id));
          if (res.ok) entry.resolve(res.result);
          else entry.reject(new MeshLinkError(normalizeRpcCode(res.error?.code), res.error?.message));
        } catch (err) {
          logger.warn('RPC 响应解析失败', { err });
        }
      };
      ws.onerror = () => {
        clearTimeout(timer);
        this.connectPromise = null;
        reject(new MeshLinkError('E_CHAIN_UNREACHABLE'));
      };
      ws.onclose = () => {
        clearTimeout(timer);
        this.ws = null;
        this.connectPromise = null;
        for (const [, entry] of this.pending) {
          entry.reject(new MeshLinkError('E_CHAIN_UNREACHABLE'));
        }
        this.pending.clear();
      };
    });
    return this.connectPromise;
  }

  /** 发起一次 RPC 调用。 */
  async call<T>(method: string, params: unknown[] = []): Promise<T> {
    const ws = await this.connect();
    const id = (this.seq += 1);
    return new Promise<T>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.pending.delete(id);
        reject(new MeshLinkError('E_CHAIN_UNREACHABLE', '链服务响应超时'));
      }, 8_000);
      this.pending.set(id, {
        resolve: (v) => {
          clearTimeout(timer);
          resolve(v as T);
        },
        reject: (e) => {
          clearTimeout(timer);
          reject(e);
        },
      });
      ws.send(JSON.stringify({ id, method, params }));
    });
  }

  /** 关闭连接。 */
  async close(): Promise<void> {
    this.ws?.close();
    this.ws = null;
    this.connectPromise = null;
    this.pending.clear();
  }

  /** 健康检查（不抛错）。 */
  async isAvailable(): Promise<boolean> {
    try {
      await this.call<string>('ping');
      return true;
    } catch {
      return false;
    }
  }

  // ─────────────── 账户 ───────────────

  async registerAccount(address: Address, pubkeyB64: string): Promise<{ success: boolean; txHash?: TxHash }> {
    const res = await this.call<{ success: boolean; txHash?: TxHash }>('registerAccount', [address, pubkeyB64]);
    return res;
  }

  async getBalance(address: Address): Promise<Micro> {
    return this.call<Micro>('getBalance', [address]);
  }

  async getBlockHeight(): Promise<number> {
    return this.call<number>('getBlockHeight');
  }

  // ─────────────── 资产 ───────────────

  async transfer(req: {
    from: Address;
    to: Address;
    amountMicro: Micro;
    signature: string;
  }): Promise<TxRecord> {
    return this.call<TxRecord>('transfer', [req]);
  }

  async faucet(address: Address, amountMicro?: Micro): Promise<{ txHash: TxHash; amountMicro: Micro }> {
    const res = await this.call<{ txHash: TxHash; amountMicro: Micro }>('faucet', [address, amountMicro]);
    return res;
  }

  async getTransactionHistory(address: Address, limit = 50): Promise<TxRecord[]> {
    return this.call<TxRecord[]>('getTransactionHistory', [address, limit]);
  }

  // ─────────────── 内容存证 ───────────────

  async anchor(req: { author: Address; cid: string; signature: string; kind?: AnchorKind }): Promise<{ txHash: TxHash; blockHeight: number; feeMicro: Micro }> {
    return this.call('anchor', [req]);
  }

  async getAnchor(cid: string): Promise<Anchor | null> {
    return this.call<Anchor | null>('getAnchor', [cid]);
  }

  // ─────────────── 创作者付费内容 ───────────────

  async purchaseContent(req: ContentPurchaseReq): Promise<ContentPurchaseRecord> {
    return this.call<ContentPurchaseRecord>('purchaseContent', [req]);
  }

  async hasPurchased(contentCid: string, buyer: Address): Promise<ContentPurchaseRecord | null> {
    return this.call<ContentPurchaseRecord | null>('hasPurchased', [contentCid, buyer]);
  }

  // ─────────────── 社交图谱 ───────────────

  async follow(req: { action: 'follow' | 'unfollow'; follower: Address; followee: Address; signature: string }): Promise<{ txHash: TxHash }> {
    return this.call('follow', [req]);
  }

  async unfollow(req: { action: 'follow' | 'unfollow'; follower: Address; followee: Address; signature: string }): Promise<{ txHash: TxHash }> {
    return this.call('unfollow', [req]);
  }

  async getFollowing(follower: Address): Promise<Address[]> {
    return this.call<Address[]>('getFollowing', [follower]);
  }

  async getFollowerCount(followee: Address): Promise<number> {
    return this.call<number>('getFollowerCount', [followee]);
  }

  // ─────────────── 全行为挖矿：行为上链 ───────────────

  async socialAct(req: {
    action: 'follow' | 'unfollow' | 'like' | 'collect' | 'subscribe' | 'share';
    from: Address;
    target: string;
    signature: string;
  }): Promise<{ txHash: TxHash }> {
    return this.call<{ txHash: TxHash }>('socialAct', [req]);
  }

  async referral(req: {
    from: Address;
    referrer: Address;
    signature: string;
  }): Promise<{ txHash: TxHash }> {
    return this.call<{ txHash: TxHash }>('referral', [req]);
  }

  async relayStake(req: {
    action: 'stake' | 'unstake' | 'slash';
    address: Address;
    amountMicro?: string;
    peerId?: string;
    reason?: string;
    signature?: string;
  }): Promise<{ txHash: TxHash }> {
    return this.call<{ txHash: TxHash }>('relayStake', [req]);
  }

  async browse(req: {
    from: Address;
    cid: string;
    duration: number;
    ratio: number;
    signature?: string;
  }): Promise<{ txHash: TxHash; blockHeight?: number }> {
    return this.call<{ txHash: TxHash; blockHeight?: number }>('browse', [req]);
  }

  async search(req: {
    from: Address;
    termHash: string;
    signature?: string;
  }): Promise<{ txHash: TxHash; blockHeight?: number }> {
    return this.call<{ txHash: TxHash; blockHeight?: number }>('search', [req]);
  }

  async checkin(req: {
    from: Address;
    ts?: number;
    signature?: string;
  }): Promise<{ txHash: TxHash; blockHeight?: number }> {
    return this.call<{ txHash: TxHash; blockHeight?: number }>('checkin', [req]);
  }

  async getContentConfig(): Promise<ContentConfig> {
    return this.call<ContentConfig>('getContentConfig', []);
  }

  async getAppConfig(): Promise<AppConfig> {
    return this.call<AppConfig>('getAppConfig', []);
  }

  // ─────────────── 支付通道 ───────────────

  async openChannel(req: {
    payer: Address;
    relay: string;
    depositMicro: Micro;
    unitPriceMicroPerMB: number;
    maxBytes: number;
    signature: string;
  }): Promise<{ channelId: string; txHash: TxHash }> {
    return this.call('openChannel', [req]);
  }

  async reportMetering(req: { channelId: string; bytes: number; seq: number; signature: string }): Promise<{ accepted: boolean; remainingMicro: Micro }> {
    return this.call('reportMetering', [req]);
  }

  async closeChannel(req: { channelId: string; signature: string }): Promise<{ txHash: TxHash; refundedMicro: Micro }> {
    return this.call('closeChannel', [req]);
  }

  async getChannel(channelId: string): Promise<Channel | null> {
    return this.call<Channel | null>('getChannel', [channelId]);
  }

  // ─────────────── DEX 交易（生态门户集成） ───────────────

  async dexMarkets(): Promise<DexPool[]> {
    return this.call<DexPool[]>('dexMarkets', []);
  }

  async dexCandles(pool: string, interval = 3600, limit = 240): Promise<DexCandle[]> {
    return this.call<DexCandle[]>('dexCandles', [pool, interval, limit]);
  }

  async dexOrders(pool: string, mine?: Address): Promise<DexOrder[]> {
    return this.call<DexOrder[]>('dexOrders', [pool, mine]);
  }

  async dexTrades(pool?: string, limit = 20): Promise<DexTrade[]> {
    return this.call<DexTrade[]>('dexTrades', [pool, limit]);
  }

  async dexSubmit(tx: Record<string, unknown>): Promise<{ id?: string; tx?: Record<string, unknown>; ts?: number; source?: string }> {
    return this.call('dexSubmit', [tx]);
  }

  // ─────────────── 全行为挖矿 / 生态展示 ───────────────

  async meshStats(): Promise<Record<string, unknown>> {
    return this.call<Record<string, unknown>>('meshStats', []);
  }

  async myRewards(address: Address, limit = 50): Promise<BehaviorRewardEvent[]> {
    return this.call<BehaviorRewardEvent[]>('myRewards', [address, limit]);
  }

  async recommendedFeed(limit = 20): Promise<FeedItem[]> {
    return this.call<FeedItem[]>('recommendedFeed', [limit]);
  }

  // ─────────────── 商家推广（v3.5） ───────────────

  async promoCreate(req: PromoCreateReq): Promise<{ txHash: TxHash; promoId: string }> {
    return this.call<{ txHash: TxHash; promoId: string }>('promoCreate', [req]);
  }

  async promoRedeem(req: PromoRedeemReq): Promise<{ txHash: TxHash; splits: Record<string, string> }> {
    return this.call<{ txHash: TxHash; splits: Record<string, string> }>('promoRedeem', [req]);
  }

  async promoList(): Promise<Record<string, unknown>> {
    return this.call<Record<string, unknown>>('promoList', []);
  }

  // ─────────────── v3.38 商家飞轮 ───────────────

  async groupon(req: GrouponReq): Promise<{ txHash: TxHash }> {
    return this.call<{ txHash: TxHash }>('groupon', [req]);
  }

  async subscription(req: SubscriptionReq): Promise<{ txHash: TxHash }> {
    return this.call<{ txHash: TxHash }>('subscription', [req]);
  }

  async cross(req: CrossReq): Promise<{ txHash: TxHash }> {
    return this.call<{ txHash: TxHash }>('cross', [req]);
  }

  async stakeInterest(req: StakeInterestReq): Promise<{ txHash: TxHash }> {
    return this.call<{ txHash: TxHash }>('stakeInterest', [req]);
  }

  async listAnchors(limit = 100): Promise<Array<Record<string, unknown>>> {
    return this.call<Array<Record<string, unknown>>>('listAnchors', [limit]);
  }

  // ─────────────── 内容举报（治理闭环） ───────────────

  async reportContent(req: ReportReq): Promise<ReportResult> {
    return this.call<ReportResult>('reportContent', [req]);
  }

  /** 作者申诉（治理 v2）：透传服务端 RPC（链端校验作者身份/窗口）。 */
  async appealContent(req: AppealReq): Promise<AppealResult> {
    return this.call<AppealResult>('appealContent', [req]);
  }

  /** 仲裁裁决（治理 v2）：仅运营通道可达（/admin/rpc adminResolveReport）；普通用户 RPC 无此方法。 */
  async resolveReport(): Promise<ResolveResult> {
    throw new Error('仲裁裁决仅运营通道（管理台）可用');
  }

  /** DPB → 微单位（便于 UI 构造转账金额）。统一走 `dpbToMicro`，保证口径唯一。 */
  static toMicro(dpb: string): Micro {
    return dpbToMicro(dpb).toString();
  }
}

/** 默认共享账本客户端单例。 */
export const mockChainRpcClient = new MockChainRpcClient();
export default mockChainRpcClient;

/** 便于测试：直接构造一个指向指定地址的客户端。 */
export function createChainRpcClient(url?: string): MockChainRpcClient {
  return new MockChainRpcClient(url);
}

export { throwRpcError };
